// Pestaña Referidos del cajón del cliente, la misma en el portal SK y en el VL: totales de premios,
// quién lo recomendó, una tarjeta por referido con lo que ganó cada lado, y agregar o corregir.
import { useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { Button, HelpTooltip, Skeleton, usePermissions } from '@/kit';
import type { BackofficeCustomerReferral } from '@/api/services/retail-wizard/customerReferrals.client';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { formatPhone } from '@/shared/lib/format/phone';
import { UnavailableState } from '@/shared/patterns/DataTable';
import { DetailCard } from '@/shared/patterns/DetailCard';
import { STATUS_PILL_TONE_CLASS, StatusPill } from '@/shared/patterns/Pills';
import {
    captureLine,
    CUSTOMER_REFERRAL_WRITE_PERMISSION,
    firstNameOf,
    REFERRAL_STATUS_LABEL,
    REFERRAL_STATUS_TONE,
    REFERRALS_TAB_COPY as COPY,
    REFERRALS_FAILURE_COPY,
    sortByName,
} from './customerReferrals';
import { ReferralDialog } from './ReferralDialog';
import {
    REWARDS_COPY,
    referralRewardsOf,
    referredByRewardsOf,
    rewardTotalsOf,
    type RewardItem,
    type RewardTotals,
} from './referralRewards';
import { useCustomerReferrals, type ReferralsScope } from './useCustomerReferrals';

export interface CustomerReferralsTabProps {
    retailCustomerId: string;
    customerName: string;
    customerPhone: string | null;
    scope?: ReferralsScope;
}

type DialogState =
    | { mode: 'closed' }
    | { mode: 'add' }
    | { mode: 'edit'; referral: BackofficeCustomerReferral };

const FIELD_LABEL = 'text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground';

const AMOUNT_TONE: Record<RewardItem['tone'], string> = {
    success: 'text-success',
    muted: 'text-muted-foreground',
};

/** «—» con el porqué en un tooltip: promociones no respondió, no es que no haya premio. */
function RewardsUnavailable() {
    return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
            <span aria-hidden>—</span>
            <HelpTooltip
                content={REWARDS_COPY.unavailableHint}
                label={REWARDS_COPY.unavailableLabel}
            />
        </span>
    );
}

function TotalPanel({
    title,
    cents,
    count,
    countLabel,
    zeroLabel,
    tone,
    unavailable,
}: {
    title: string;
    cents: number;
    count: number;
    countLabel: (n: number) => string;
    zeroLabel: string;
    tone: 'success' | 'info';
    unavailable: boolean;
}) {
    const hasValue = !unavailable && count > 0;
    return (
        <section
            aria-label={title}
            className={cn(
                'min-w-0 rounded-md border px-3 py-2.5',
                hasValue
                    ? tone === 'success'
                        ? 'border-success-soft-border bg-success-soft'
                        : 'border-info-soft-border bg-info-soft'
                    : 'border-border bg-card',
            )}
        >
            <h4 className={FIELD_LABEL}>{title}</h4>
            {unavailable ? (
                <p className="mt-1 text-xl font-bold">
                    <RewardsUnavailable />
                </p>
            ) : (
                <>
                    <p
                        className={cn(
                            'mt-0.5 text-xl font-bold tabular-nums',
                            hasValue
                                ? tone === 'success'
                                    ? 'text-success'
                                    : 'text-info'
                                : 'text-muted-foreground',
                        )}
                    >
                        {formatMoneyCents(cents)}
                    </p>
                    <p className="text-[10.5px] text-muted-foreground">
                        {count > 0 ? countLabel(count) : zeroLabel}
                    </p>
                </>
            )}
        </section>
    );
}

function RewardTotalsPanels({ totals, customer }: { totals: RewardTotals; customer: string }) {
    const unavailable = totals.kind === 'unavailable';
    const ready = totals.kind === 'ready' ? totals : null;
    return (
        <div className="mb-2.5 grid gap-2 @md:grid-cols-2">
            <TotalPanel
                title={REWARDS_COPY.earnedTitle(customer)}
                cents={ready?.earnedCents ?? 0}
                count={ready?.earnedCount ?? 0}
                countLabel={REWARDS_COPY.earnedCount}
                zeroLabel={REWARDS_COPY.earnedZero}
                tone="success"
                unavailable={unavailable}
            />
            <TotalPanel
                title={REWARDS_COPY.givenTitle}
                cents={ready?.givenCents ?? 0}
                count={ready?.givenCount ?? 0}
                countLabel={REWARDS_COPY.givenCount}
                zeroLabel={REWARDS_COPY.givenZero}
                tone="info"
                unavailable={unavailable}
            />
        </div>
    );
}

function RewardSide({ title, items }: { title: string; items: RewardItem[] }) {
    return (
        <div className="min-w-0 px-3 py-2">
            <p className={FIELD_LABEL}>{title}</p>
            {items.length === 0 ? (
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                    {REWARDS_COPY.sideEmpty}
                </p>
            ) : (
                <ul className="mt-0.5 space-y-1.5">
                    {items.map((item) => (
                        <li key={item.key} title={item.promotionName}>
                            <span
                                className={cn(
                                    'block text-[15px] font-bold leading-tight tabular-nums',
                                    AMOUNT_TONE[item.tone],
                                )}
                            >
                                {item.amount}
                            </span>
                            <span className="block text-[10px] leading-snug text-muted-foreground">
                                {item.detail}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ReferralCard({
    referral,
    customerFirstName,
    onEdit,
}: {
    referral: BackofficeCustomerReferral;
    customerFirstName: string;
    onEdit: (() => void) | null;
}) {
    const rewards = referralRewardsOf(referral.rewards);
    const capture = captureLine(referral.capture);
    const titleId = `referral-${referral.referralId}`;
    return (
        <li>
            <article
                aria-labelledby={titleId}
                className="overflow-hidden rounded-md border border-border bg-card"
            >
                <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                        <h4 id={titleId} className="text-[12.5px] font-semibold leading-tight">
                            {referral.name}
                        </h4>
                        <p className="text-[10.5px] tabular-nums text-muted-foreground">
                            {formatPhone(referral.phone)}
                            {referral.email ? (
                                <span className="[overflow-wrap:anywhere]">
                                    {' '}
                                    · {referral.email}
                                </span>
                            ) : null}
                        </p>
                        <p
                            className="text-[10.5px] leading-snug text-muted-foreground [overflow-wrap:anywhere]"
                            title={capture}
                        >
                            {capture}
                        </p>
                        {referral.capture.source === 'BACKOFFICE' ? (
                            <span
                                title={COPY.noCommissionHint}
                                className="mt-1 inline-block rounded-sm bg-muted px-1 py-[1px] text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                            >
                                {COPY.noCommission}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusPill tone={REFERRAL_STATUS_TONE[referral.status]}>
                            {REFERRAL_STATUS_LABEL[referral.status]}
                        </StatusPill>
                        {rewards.kind === 'none' ? (
                            <StatusPill tone="neutral">{REWARDS_COPY.noneLabel}</StatusPill>
                        ) : null}
                        {rewards.kind === 'unavailable' ? <RewardsUnavailable /> : null}
                        {onEdit ? (
                            <button
                                type="button"
                                aria-label={COPY.editLabel(referral.name)}
                                onClick={onEdit}
                                className="whitespace-nowrap rounded text-[10.5px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                {COPY.edit}
                            </button>
                        ) : null}
                    </div>
                </div>
                {rewards.kind === 'split' ? (
                    <>
                        <div className="grid border-t border-border/60 @md:grid-cols-2">
                            <div className="border-b border-border/60 @md:border-b-0 @md:border-r">
                                <RewardSide
                                    title={REWARDS_COPY.sideTitle(customerFirstName)}
                                    items={rewards.referrer}
                                />
                            </div>
                            <RewardSide
                                title={REWARDS_COPY.sideTitle(firstNameOf(referral.name))}
                                items={rewards.referred}
                            />
                        </div>
                        <p className="border-t border-border/60 bg-muted/30 px-3 py-1.5 text-[10px] text-muted-foreground">
                            {REWARDS_COPY.promotions(rewards.promotions)}
                        </p>
                    </>
                ) : null}
            </article>
        </li>
    );
}

export function CustomerReferralsTab({
    retailCustomerId,
    customerName,
    customerPhone,
    scope = {},
}: CustomerReferralsTabProps) {
    const referrals = useCustomerReferrals(retailCustomerId, scope);
    const { hasPermission } = usePermissions();
    const canWrite = hasPermission(CUSTOMER_REFERRAL_WRITE_PERMISSION);
    const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
    const rows = useMemo(
        () => sortByName(referrals.data?.referrals ?? []),
        [referrals.data?.referrals],
    );
    const totals = useMemo(() => rewardTotalsOf(rows), [rows]);
    const retailerId = scope.retailerId ?? undefined;
    const customerFirstName = firstNameOf(customerName);

    if (referrals.hasNoScope) {
        return (
            <DetailCard title={COPY.listTitle}>
                <UnavailableState title={COPY.noScopeTitle} body={COPY.noScopeText} />
            </DetailCard>
        );
    }
    if (referrals.failure) {
        const failure = REFERRALS_FAILURE_COPY[referrals.failure];
        return (
            <DetailCard title={COPY.listTitle}>
                <UnavailableState
                    title={failure.title}
                    body={failure.body}
                    action={
                        failure.canRetry ? (
                            <Button variant="outline" size="sm" onClick={referrals.refetch}>
                                {COPY.retry}
                            </Button>
                        ) : undefined
                    }
                />
            </DetailCard>
        );
    }

    const addButton = canWrite ? (
        <Button
            size="sm"
            leftIcon={<Plus className="size-3.5" aria-hidden />}
            onClick={() => {
                setDialog({ mode: 'add' });
            }}
        >
            {COPY.add}
        </Button>
    ) : null;

    const referredBy = referrals.data?.referredBy ?? null;
    const referredTags = referredBy
        ? referredByRewardsOf(referredBy.rewards, {
              customer: customerFirstName,
              referrer: firstNameOf(referredBy.referrerName),
          })
        : null;
    const isEmpty = !referrals.isLoading && rows.length === 0;

    return (
        <div className="@container">
            {referrals.isLoading ? null : (
                <RewardTotalsPanels totals={totals} customer={customerFirstName} />
            )}

            {referredBy && referredTags ? (
                <DetailCard title={COPY.referredByTitle}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="break-words text-[12px] font-semibold">
                                {referredBy.referrerName}
                            </p>
                            <p className="text-[10.5px] text-muted-foreground">
                                {formatDateMx(referredBy.referredAt)}
                            </p>
                        </div>
                        {referredTags.kind === 'tags' ? (
                            <ul className="flex min-w-0 flex-col items-end gap-1">
                                {referredTags.tags.map((tag) => (
                                    <li
                                        key={tag.key}
                                        title={tag.title}
                                        className={cn(
                                            'rounded-full border px-2 py-px text-right text-[10.5px] font-medium',
                                            STATUS_PILL_TONE_CLASS[tag.tone],
                                        )}
                                    >
                                        {tag.text}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="max-w-[60%] text-right text-[10.5px] text-muted-foreground">
                                {referredTags.kind === 'none'
                                    ? REWARDS_COPY.referredNone
                                    : REWARDS_COPY.referredUnavailable}
                            </p>
                        )}
                    </div>
                </DetailCard>
            ) : null}

            <DetailCard
                title={COPY.listTitle}
                meta={referrals.isLoading ? undefined : COPY.count(rows.length)}
                footnote={isEmpty ? undefined : COPY.footnote}
            >
                {isEmpty ? (
                    <UnavailableState
                        icon={
                            <Users className="mb-3 size-10 text-muted-foreground/40" aria-hidden />
                        }
                        title={COPY.emptyTitle}
                        body={canWrite ? COPY.emptyTextWrite : COPY.emptyText}
                        action={addButton ?? undefined}
                    />
                ) : (
                    <>
                        {addButton ? (
                            <div className="mb-2 flex justify-end">{addButton}</div>
                        ) : null}
                        {referrals.isLoading ? (
                            <Skeleton lines={3} className="h-16" />
                        ) : (
                            <ul aria-label={COPY.listLabel} className="space-y-2">
                                {rows.map((r) => (
                                    <ReferralCard
                                        key={r.referralId}
                                        referral={r}
                                        customerFirstName={customerFirstName}
                                        onEdit={
                                            canWrite
                                                ? () => {
                                                      setDialog({ mode: 'edit', referral: r });
                                                  }
                                                : null
                                        }
                                    />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </DetailCard>

            {dialog.mode === 'closed' ? null : (
                <ReferralDialog
                    retailCustomerId={retailCustomerId}
                    retailerId={retailerId}
                    customerName={customerName}
                    customerPhone={customerPhone}
                    editing={dialog.mode === 'edit' ? dialog.referral : null}
                    referrals={rows}
                    onClose={() => {
                        setDialog({ mode: 'closed' });
                    }}
                />
            )}
        </div>
    );
}
